import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { MainSteps, PaymentSubSteps } from '@models/config/app-structure.model';
import { ApplicationService } from '@services/api-calls/application.service';
import { PaymentDataService } from '@services/api-calls/payment-data.service';
import { PayerPayload, PaymentInformationPayload, PaymentService } from '@services/api-calls/payment.service';
import { ErrorHandlingService } from '@services/error-handling.service';
import { ApplicationStateService } from '@services/application-state.service';
import { ApplicationFormService } from '@services/form/application-form.service';
import { FormRegistryService } from '@services/form/form-registry.service';
import { DateUtil } from '@utils/date.util';
import { PaymentDateUtil } from '@utils/payment-date.util';
import {
    AchDisclaimerFormEnum,
    // IAPP2-1744: Additional person removed
    // AdditionalPersonFormEnum,
    AddressFormEnum,
    PaymentDateEnum,
    PaymentFrequencyEnum,
    PaymentFrequencyValueEnum,
    PaymentInformationFormEnum,
    // IAPP2-1744: SSD payment removed
    // SsdDateOptionsEnum,
    YesNoOptionsEnum,
} from 'app/enums/form/form.enum';
import { catchError, concatMap, Observable, of, tap } from 'rxjs';

const API_URL = environment.apiUrl;

@Injectable({
    providedIn: 'root',
})
export class PaymentSaveService {
    constructor(
        private http: HttpClient,
        private paymentService: PaymentService,
        private applicationService: ApplicationService,
        private applicationFormService: ApplicationFormService,
        private formRegistry: FormRegistryService,
        private errorHandlingService: ErrorHandlingService,
        private paymentDataService: PaymentDataService,
        private applicationStateService: ApplicationStateService,
    ) {}

    updateHasPaymentDisclosures(value: number, options?: { silent?: boolean }): Observable<unknown> | undefined {
        const applicationId = this.applicationService.applicationId();
        if (!applicationId) return undefined;

        const obs = this.http.patch(`${API_URL}/applications/${Number(applicationId)}/payment`, { has_payment_disclosures: value });

        if (options?.silent) {
            return obs.pipe(catchError((error) => this.errorHandlingService.handleSaveError(error)));
        }

        return obs.pipe(
            tap(() => this.applicationService.refreshApplicationData.update((n) => n + 1)),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    /** Save the user's YES/NO answer to "Do you want to use a secondary payment method?".
     *  1 = YES, 0 = NO. Backend stores this on app_payment.secondary_payment_selected so
     *  the absence of a secondary payer can be distinguished from "hasn't answered yet".
     */
    updateSecondaryPaymentSelected(value: number, options?: { silent?: boolean }): Observable<unknown> | undefined {
        const applicationId = this.applicationService.applicationId();
        if (!applicationId) return undefined;

        const obs = this.http.patch(
            `${API_URL}/applications/${Number(applicationId)}/payment`,
            { secondary_payment_selected: value },
        );

        if (options?.silent) {
            return obs.pipe(catchError((error) => this.errorHandlingService.handleSaveError(error)));
        }

        return obs.pipe(
            tap(() => this.applicationService.refreshApplicationData.update((n) => n + 1)),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    // IAPP2-1744: SSD and additional person removed - simplified
    savePaymentInformation(): Observable<unknown> | undefined {
        const applicationId = this.applicationService.applicationId();
        if (!applicationId) {
            return undefined;
        }

        const appId = Number(applicationId);
        const paymentInfoChanged = this.hasPaymentInformationChanged();

        return paymentInfoChanged ? this.savePaymentInfo(appId) : undefined;
    }

    // IAPP2-1744: Additional person removed
    // private shouldSavePayerContact(): boolean {
    //     const paymentFormGroup = this.getPaymentInformationFormGroup();
    //     if (!paymentFormGroup) return false;

    //     const designateAdditionalPerson = paymentFormGroup.get(PaymentInformationFormEnum.DESIGNATE_ADDITIONAL_PERSON)?.value;
    //     const additionalPersonGroup = paymentFormGroup.get(PaymentInformationFormEnum.ADDITIONAL_PERSON_GROUP);

    //     return designateAdditionalPerson === YesNoOptionsEnum.YES && additionalPersonGroup?.valid === true;
    // }

    // private shouldDeleteExistingPayerContact(): boolean {
    //     const paymentFormGroup = this.getPaymentInformationFormGroup();
    //     if (!paymentFormGroup) return false;

    //     const designateAdditionalPerson = paymentFormGroup.get(PaymentInformationFormEnum.DESIGNATE_ADDITIONAL_PERSON)?.value;
    //     if (designateAdditionalPerson !== YesNoOptionsEnum.NO) return false;

    //     const appPayment = this.paymentDataService.applicationPayment();
    //     const payerContactId = appPayment?.payer_contact_id;
    //     const insuredClientId = this.applicationService.application()?.insured_client_id;

    //     return payerContactId !== null && payerContactId !== undefined && payerContactId !== insuredClientId;
    // }

    // private deletePayerContact(applicationId: number): Observable<unknown> {
    //     const appPayment = this.paymentDataService.applicationPayment();
    //     const payerContactId = appPayment?.payer_contact_id;
    //     if (!payerContactId) {
    //         return of(null);
    //     }
    //     return this.paymentService.deleteClient(applicationId, payerContactId);
    // }

    // private savePayerContact(applicationId: number): Observable<unknown> {
    //     const payload = this.buildPayerContactPayload();
    //     return this.paymentService.savePayerContact(applicationId, payload);
    // }

    private savePaymentInfo(applicationId: number): Observable<unknown> {
        const payload = this.buildPaymentInformationPayload();
        return this.paymentService.savePaymentInformation(applicationId, payload).pipe(
            tap(() => {
                // Lock previous steps after successful payment save
                this.applicationStateService.setPaymentStarted(true);
            }),
        );
    }

    // IAPP2-1744: Additional person removed
    // private buildPayerContactPayload(): Omit<PayerPayload, 'role'> {
    //     const paymentFormGroup = this.getPaymentInformationFormGroup();
    //     const additionalPersonGroup = paymentFormGroup?.get(PaymentInformationFormEnum.ADDITIONAL_PERSON_GROUP);
    //     const addressGroup = additionalPersonGroup?.get(AdditionalPersonFormEnum.ADDRESS_GROUP);

    //     return {
    //         first_name: additionalPersonGroup?.get(AdditionalPersonFormEnum.FIRST_NAME)?.value,
    //         middle_name: additionalPersonGroup?.get(AdditionalPersonFormEnum.MIDDLE_NAME)?.value,
    //         last_name: additionalPersonGroup?.get(AdditionalPersonFormEnum.LAST_NAME)?.value,
    //         relationship: additionalPersonGroup?.get(AdditionalPersonFormEnum.RELATIONSHIP)?.value,
    //         address: addressGroup?.get(AddressFormEnum.ADDRESS)?.value,
    //         address2: addressGroup?.get(AddressFormEnum.ADDRESS2)?.value,
    //         city: addressGroup?.get(AddressFormEnum.CITY)?.value,
    //         country_code: addressGroup?.get(AddressFormEnum.COUNTRY_CODE)?.value,
    //         state_code: addressGroup?.get(AddressFormEnum.STATE_CODE)?.value,
    //         zip_code: addressGroup?.get(AddressFormEnum.ZIP_CODE)?.value,
    //     };
    // }

    // IAPP2-1744: SSD payment removed - simplified
    private buildPaymentInformationPayload(): PaymentInformationPayload {
        const initialDate = this.getInitialPaymentDate();
        const recurringDate = this.getSubsequentPaymentDate();
        const paymentFrequency = this.getPaymentFrequencyValue();
        const paymentDate = this.getPaymentDateValue();
        const hasPaymentDisclosures = this.getHasPaymentDisclosuresValue();

        const payload: PaymentInformationPayload = {
            initial_payment_at: initialDate ? DateUtil.toServer(initialDate) : null,
            initial_payment_day: this.getInitialPaymentDay(),
            initial_payment_day_details: null, // IAPP2-1744: SSD payment removed
            draw_at: recurringDate ? DateUtil.toServer(recurringDate) : null,
            draw_recurring_day: this.getRecurringPaymentDay(),
            draw_recurring_day_details: null, // IAPP2-1744: SSD payment removed
            payment_ssd: 0, // IAPP2-1744: SSD payment removed - always 0
            payment_frequency: this.getPaymentFrequencyNumericValue(paymentFrequency),
            payment_date_options: paymentDate ?? PaymentDateEnum.AT_TIME_OF_SALE,
        };

        // Only include if value is set (0 or 1)
        if (hasPaymentDisclosures !== null) {
            payload.has_payment_disclosures = hasPaymentDisclosures;
        }

        return payload;
    }

    private getPaymentInformationFormGroup() {
        const paymentMainGroup = this.applicationFormService.getMainStepFormGroup(MainSteps.PAYMENT);
        return paymentMainGroup?.get(PaymentSubSteps.PAYMENT_INFORMATION);
    }

    private getPaymentFrequencyValue(): string | null {
        const signal = this.formRegistry.getControlValue<string | null>([
            MainSteps.PAYMENT,
            PaymentSubSteps.PAYMENT_INFORMATION,
            PaymentInformationFormEnum.PAYMENT_FREQUENCY,
        ]);
        return signal?.() ?? null;
    }

    private getPaymentDateValue(): string | null {
        const signal = this.formRegistry.getControlValue<string | null>([
            MainSteps.PAYMENT,
            PaymentSubSteps.PAYMENT_INFORMATION,
            PaymentInformationFormEnum.PAYMENT_DATE,
        ]);
        return signal?.() ?? null;
    }

    // IAPP2-1744: SSD payment removed
    // private getSsdCheckboxValue(): boolean | null {
    //     const signal = this.formRegistry.getControlValue<boolean | null>([
    //         MainSteps.PAYMENT,
    //         PaymentSubSteps.PAYMENT_INFORMATION,
    //         PaymentInformationFormEnum.SOCIAL_SECURITY_AND_DISABILITY,
    //     ]);
    //     return signal?.() ?? null;
    // }

    private getFutureDateValue(): string | Date | null {
        const paymentFormGroup = this.getPaymentInformationFormGroup();
        const futureDateControl = paymentFormGroup?.get(PaymentInformationFormEnum.FUTURE_PAYMENT_DATE);

        // The control is added by PaymentInformationComponent and only exists while that substep is
        // mounted. When the payload is built from another substep — e.g. after a page reload lands
        // the user past Payment Information — fall back to the persisted date so the save never
        // blanks out a future payment date the user already chose.
        if (!futureDateControl) {
            return this.paymentDataService.applicationPayment()?.initial_payment_at ?? null;
        }

        return futureDateControl.value ?? null;
    }

    // IAPP2-1744: SSD payment removed
    // private getFutureDateSsdValue(): number | null {
    //     const paymentFormGroup = this.getPaymentInformationFormGroup();
    //     return paymentFormGroup?.get(PaymentInformationFormEnum.FUTURE_PAYMENT_DATE_ON_DISABILITY)?.value ?? null;
    // }

    private getRecurringDateValue(): number | null {
        const paymentFormGroup = this.getPaymentInformationFormGroup();
        return paymentFormGroup?.get(PaymentInformationFormEnum.RECURRING_PAYMENT_DATE)?.value ?? null;
    }

    // IAPP2-1744: SSD payment removed
    // private getRecurringDateSsdValue(): number | null {
    //     const paymentFormGroup = this.getPaymentInformationFormGroup();
    //     return paymentFormGroup?.get(PaymentInformationFormEnum.RECURRING_PAYMENT_DATE_ON_DISABILITY)?.value ?? null;
    // }

    // IAPP2-1744: SSD payment removed - simplified
    private getInitialPaymentDate(): Date | null {
        const paymentDate = this.getPaymentDateValue();
        const futureDateValue = this.getFutureDateValue();

        if (paymentDate === PaymentDateEnum.AT_TIME_OF_SALE) {
            const today = new Date();
            if ([29, 30, 31].includes(today.getDate())) {
                return PaymentDateUtil.getFirstDayOfNextMonth();
            }
            return today;
        } else if (paymentDate === PaymentDateEnum.FUTURE_PAYMENT_DATE) {
            if (futureDateValue) {
                return futureDateValue instanceof Date ? futureDateValue : DateUtil.fromServer(futureDateValue);
            }
        }
        return null;
    }

    // IAPP2-1744: SSD payment removed - simplified
    private getSubsequentPaymentDate(): Date | null {
        const initialDate = this.getInitialPaymentDate();
        const recurringDay = this.getRecurringDateValue();

        if (!initialDate) {
            return null;
        }

        if (recurringDay) {
            return PaymentDateUtil.calculateFirstRecurringDate(initialDate, recurringDay);
        }

        // IAPP2-XXX: Recurring Payment Date hidden — fallback to initial + 1 month
        return PaymentDateUtil.calculateEndOfBillingCycle(initialDate);
    }

    private getPaymentFrequencyNumericValue(paymentFrequency: string | null): number {
        return paymentFrequency === PaymentFrequencyEnum.ANNUAL ? PaymentFrequencyValueEnum.ANNUAL : PaymentFrequencyValueEnum.MONTHLY;
    }

    // IAPP2-1744: SSD payment removed - simplified
    private getInitialPaymentDay(): number | null {
        const paymentDate = this.getPaymentDateValue();

        if (paymentDate === PaymentDateEnum.FUTURE_PAYMENT_DATE) {
            const futureDate = this.getFutureDateValue();
            if (futureDate) {
                const date = typeof futureDate === 'string' ? DateUtil.fromServer(futureDate) : futureDate;
                return date?.getDate() ?? null;
            }
        }
        return null;
    }

    // IAPP2-1744: SSD payment removed
    // private getInitialPaymentDayDetails(): string | null {
    //     const ssdChecked = this.getSsdCheckboxValue();
    //     if (!ssdChecked) return null;

    //     const ssdOption = this.getFutureDateSsdValue();
    //     switch (ssdOption) {
    //         case SsdDateOptionsEnum.SECOND_WEDNESDAY:
    //         case SsdDateOptionsEnum.THIRD_WEDNESDAY:
    //         case SsdDateOptionsEnum.FOURTH_WEDNESDAY:
    //             return 'wednesday';
    //         default:
    //             return null;
    //     }
    // }

    // IAPP2-1744: SSD payment removed - simplified
    private getRecurringPaymentDay(): number | null {
        return this.getRecurringDateValue();
    }

    // IAPP2-1744: SSD payment removed
    // private getRecurringPaymentDayDetails(): string | null {
    //     const ssdChecked = this.getSsdCheckboxValue();
    //     const paymentDate = this.getPaymentDateValue();

    //     if (ssdChecked && paymentDate === PaymentDateEnum.AT_TIME_OF_SALE) {
    //         const ssdOption = this.getRecurringDateSsdValue();
    //         switch (ssdOption) {
    //             case SsdDateOptionsEnum.SECOND_WEDNESDAY:
    //             case SsdDateOptionsEnum.THIRD_WEDNESDAY:
    //             case SsdDateOptionsEnum.FOURTH_WEDNESDAY:
    //                 return 'wednesday';
    //             default:
    //                 return null;
    //         }
    //     }
    //     return null;
    // }

    private getHasPaymentDisclosuresValue(): number | null {
        const signal = this.formRegistry.getControlValue<number | null>([
            MainSteps.PAYMENT,
            PaymentSubSteps.ACH_DISCLAIMER,
            AchDisclaimerFormEnum.ACH_PAYMENT_DISCLAIMER_ACCEPTED,
        ]);
        return signal?.() ?? null;
    }

    private hasPaymentInformationChanged(): boolean {
        const original = this.paymentDataService.applicationPayment();
        if (!original) return true;

        const current = this.buildPaymentInformationPayload();
        const originalFrequencyNumeric = this.getPaymentFrequencyNumericValue(original.payment_frequency);

        return (
            this.normalizeDateForComparison(original.initial_payment_at) !== current.initial_payment_at ||
            this.normalizeDateForComparison(original.draw_at) !== current.draw_at ||
            original.initial_payment_day !== current.initial_payment_day ||
            original.initial_payment_day_details !== current.initial_payment_day_details ||
            original.draw_recurring_day !== current.draw_recurring_day ||
            original.draw_recurring_day_details !== current.draw_recurring_day_details ||
            (original.payment_ssd ? 1 : 0) !== current.payment_ssd ||
            originalFrequencyNumeric !== current.payment_frequency ||
            original.payment_date_options !== current.payment_date_options ||
            original.has_payment_disclosures !== current.has_payment_disclosures
        );
    }

    // IAPP2-1744: Additional person removed
    // private hasPayerContactChanged(): boolean {
    //     const original = this.paymentDataService.applicationPayment()?.payer_contact;
    //     if (!original) return true;

    //     const current = this.buildPayerContactPayload();

    //     return (
    //         original.first_name !== current.first_name ||
    //         original.middle_name !== current.middle_name ||
    //         original.last_name !== current.last_name ||
    //         original.relationship !== current.relationship ||
    //         original.address?.address !== current.address ||
    //         original.address?.address2 !== current.address2 ||
    //         original.address?.city !== current.city ||
    //         original.address?.state_code !== current.state_code ||
    //         original.address?.zip_code !== current.zip_code ||
    //         original.address?.country_code !== current.country_code
    //     );
    // }

    private normalizeDateForComparison(date: Date | string | null): string | null {
        if (!date) return null;
        const dateObj = date instanceof Date ? date : DateUtil.fromServer(date);
        return dateObj ? DateUtil.toServer(dateObj) : null;
    }
}
